import * as i0 from '@angular/core';
import { forwardRef, Directive, HostListener, NgModule } from '@angular/core';
import { NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { trimEnd, get } from 'lodash-es';
import { DateTime } from 'luxon';
import moment from 'moment';
const EmailPasswordPattern = {
  // tslint:disable-next-line:max-line-length
  EMAIL: /^(([^<>()[\]\\.,;:\s@]+(\.[^<()[\]\\.,;:\s@]+)*)|(.+))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,4}))$/,
  PASSWORD_REGEX: /^(?!^(.)\1*$)(.{7,})$/,
  PASSWORD_REPEATEDCHAR_REGEX: /^(.)\1+$/,
  PASSWORD_LENGTH_REGEX: /^.{8,}$/
};
const CreditCardPatterns = {
  VISA: /^4[0-9]{12}(?:[0-9]{3})?$/
};
const NamePattern = {
  NAME: /^[a-z](?![a-z\.\`\'\-\s]*[\.\`\'\-]{2})(?![a-z\.\`\'\-\s]*[\s]{2})[a-z\.\`\'\-\s]*$/i,
  NAME_SUFFIX: /^[a-z](?![a-z\.\`\'\-\s]*[\.\`\'\-]{2})(?![a-z\.\`\'\-\s]*[\s]{2})[a-z\.\`\'\-\s]*$/i
};
const NumberCharPattern = {
  NUMBER: /^[0-9]*$/,
  // Only Number
  BEGIN_WITH_NUMBER: /[^0-9]/,
  NON_NUMBER: /[^0-9]/g,
  LETTER: /^[a-z].*[A-Z]|[A-Z].*[a-z]*$/,
  // Only combination of small & CAPS (aA)
  SPECIAL_CHAR: /^[-!$@#%^&*()_+|~=`{}\[\]:";'<>?,.\/]*$/,
  // only special characters
  NO_SPECIAL_CHARS: /^([a-zA-Z0-9 ]*)$/,
  WITH_NUMBER: /[0-9]/,
  // number with char & special characters
  WITH_LETTER: /[a-z].*[A-Z]|[A-Z].*[a-z]/,
  // combination of small & CAPS (aA) with number & special char
  WITH_SPECIAL_CHAR: /[-!$@#%^&*()_+|~=`{}\[\]:";'<>?,.\/]/,
  // special characters with char and numbers
  LETTER_NUMBER: /^[a-zA-Z0-9\s,'\-.]*$/,
  // Small & CAPS (aA) with number and spl. char except ! # $ % & ' * + - / = ? ^ _ ` { | } ~
  WITH_LOWER_LETTER: /[a-z]/,
  // combination of small letter(a) with CAPS, number & special char
  WITH_UPPER_LETTER: /[A-Z]/,
  // combination of CAPS letter(A) with small letter, number & special char
  NON_DIGIT: /\D/g,
  START_WITH_PLUS: /^\+/
};
const MiscPattern = {
  ENDS_WITH_SPACE: /\s+$/,
  BRACKET_DOUBLEQUOTES: /[\[\]'"]/g,
  DATE: /^(0?[1-9]|1[012])[\/](0?[1-9]|[12][0-9]|3[01])[\/]\d{4}$/,
  ANNUALPASS_DOB: /^(0?[1-9]|1[012])[\/](0?[1-9]|[12][0-9]|3[01])$/
};
const ZipCodePattern = {
  ZIP: /^(?:\d{5,9}|\d{5}(?:[-]\d{4})?)$/,
  NON_US_ZIP: /^([a-zA-Z0-9\s-]){0,12}$/
};
const PhonePattern = {
  PHONE: /^(\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4}))+$/,
  NON_US_PHONE: /^[0-9-]+/,
  PHONE_ANY: /^[- +()]*[0-9][- +()0-9]*$/
};
const formFieldRanges = {
  DEFAULT_COUNTRY: 'US',
  MIN: {
    PASSWORD: 8,
    USA_PHONE: 10,
    USA_ZIP: 5,
    NON_USA_PHONE: 7
  },
  MAX: {
    SUFFIX: 25,
    FIRST_NAME: 25,
    MIDDLE_NAME: 25,
    LAST_NAME: 25,
    PASSWORD: 99,
    USA_PHONE: 10,
    PHONE: 15,
    USA_ZIP: 9,
    NON_USA_ZIP: 12,
    CITY: 50,
    EMAIL: 255,
    ZIP_ENTRY: 20,
    CVV: 4,
    PAYMENT_CARD: 20,
    DATE: 11
  },
  PASSWORDS: {
    PASSWORD_LENGTH_INDEX: 10,
    PASSWORD_FORCE_FACTOR: 10,
    PASSWORD_FLAG_COLORS: ['#F00', '#F90', '#FF0', '#9F0', '#0B0'],
    PASSWORD_FLAG_STRENGTH: ['Very Weak', 'Weak', 'Medium', 'Moderate', 'Strong'],
    DEFAULT_BACKGROUND: '#DDD'
  },
  PHONE: {
    USA_COUNTRY_CODE: 'US',
    ANY_COUNTRY_CODE: 'any'
  }
};
class GdsInputHelperDirective {
  constructor(elementRef) {
    this.elementRef = elementRef;
  }
  get value() {
    return this._value;
  }
  // using set so we trigger onTouch and onChange
  set value(val) {
    if (val !== this._value) {
      this._value = val;
      if (this.onChange) {
        this.onChange(this._value);
      }
      this.elementRef.nativeElement.value = val;
    }
  }
  writeValue(value) {
    if (value) {
      this.value = value;
    }
  }
  registerOnChange(fn) {
    this.onChange = fn;
  }
  registerOnTouched(fn) {
    this.onTouched = fn;
  }
  // name of event should match of custom event in gds-input component (@Event() valueChange: EventEmitter)
  listenForValueChange(value) {
    this.value = value;
  }
  // name of event should match of custom event in gds-input component (@Event() blurChange: EventEmitter)
  listenForBlurChange() {
    if (this.onTouched && this.onChange) {
      this.onTouched();
      this.onChange(this._value);
    }
  }
  static {
    this.ɵfac = function GdsInputHelperDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || GdsInputHelperDirective)(i0.ɵɵdirectiveInject(i0.ElementRef));
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: GdsInputHelperDirective,
      selectors: [["gds-input"]],
      hostBindings: function GdsInputHelperDirective_HostBindings(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵlistener("valueChange", function GdsInputHelperDirective_valueChange_HostBindingHandler($event) {
            return ctx.listenForValueChange($event.detail);
          })("blurChange", function GdsInputHelperDirective_blurChange_HostBindingHandler() {
            return ctx.listenForBlurChange();
          });
        }
      },
      standalone: false,
      features: [i0.ɵɵProvidersFeature([{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => GdsInputHelperDirective),
        multi: true
      }])]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(GdsInputHelperDirective, [{
    type: Directive,
    args: [{
      // eslint-disable-next-line @angular-eslint/directive-selector
      selector: 'gds-input',
      providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => GdsInputHelperDirective),
        multi: true
      }],
      standalone: false
    }]
  }], () => [{
    type: i0.ElementRef
  }], {
    listenForValueChange: [{
      type: HostListener,
      args: ['valueChange', ['$event.detail']]
    }],
    listenForBlurChange: [{
      type: HostListener,
      args: ['blurChange', []]
    }]
  });
})();
class GdsInputHelperModule {
  static {
    this.ɵfac = function GdsInputHelperModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || GdsInputHelperModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: GdsInputHelperModule
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(GdsInputHelperModule, [{
    type: NgModule,
    args: [{
      declarations: [GdsInputHelperDirective],
      imports: [],
      exports: [GdsInputHelperDirective]
    }]
  }], null, null);
})();
const validatorWithMessage = (validatorFn, errorMessage) => {
  return control => {
    const error = validatorFn(control);
    const message = control.touched ? errorMessage : null;
    return error ? {
      ...error,
      message
    } : null;
  };
};
const gdsRequiredValidator = message => {
  return validatorWithMessage(Validators.required, message);
};
const gdsMinLengthValidator = (minLength, message) => {
  return validatorWithMessage(Validators.minLength(minLength), message?.replace('##minlength##', minLength?.toString() ?? ''));
};
const gdsMaxLengthValidator = (maxLength, message) => {
  return validatorWithMessage(Validators.maxLength(maxLength), message?.replace('##maxlength##', maxLength?.toString() ?? ''));
};
const gdsPatternValidator = (pattern, message) => {
  return validatorWithMessage(Validators.pattern(pattern), message);
};
const gdsEmailValidator = (requiredError, patternError) => {
  return Validators.compose([gdsRequiredValidator(requiredError), gdsPatternValidator(EmailPasswordPattern.EMAIL, patternError)]);
};
const gdsVisaCardValidator = (requiredError, patternError) => {
  return Validators.compose([gdsRequiredValidator(requiredError), gdsPatternValidator(CreditCardPatterns.VISA, patternError)]);
};
const gdsNameValidator = (requiredError, maxLengthError, patternError) => {
  return Validators.compose([gdsRequiredValidator(requiredError), gdsMaxLengthValidator(formFieldRanges.MAX.LAST_NAME, maxLengthError), gdsPatternValidator(NamePattern.NAME, patternError)]);
};
const gdsSuffixValidator = (maxLengthError, patternError) => {
  return Validators.compose([gdsMaxLengthValidator(formFieldRanges.MAX.SUFFIX, maxLengthError), gdsPatternValidator(NamePattern.NAME_SUFFIX, patternError)]);
};
const gdsPasswordValidator = (requiredError, minLengthError) => {
  return Validators.compose([gdsRequiredValidator(requiredError), gdsMinLengthValidator(formFieldRanges.MIN.PASSWORD, minLengthError)]);
};
const gdsPhoneNumericValidator = (countryName, numericError) => {
  return validatorWithMessage(NumericValidator(countryName), numericError);
};
const gdsMonthDateValidator = dateError => {
  return validatorWithMessage(MomentValidator(), dateError);
};
const NumericValidator = countryName => {
  return control => {
    let transformedInput = control.value;
    const inProcessInput = [];
    if (countryName && countryName.toLowerCase() === 'us') {
      control.value?.split('').forEach((item, index) => {
        if (index === 0 && item === '(' || index === 4 && item === ')' || index === 5 && item === ' ' || index === 9 && item === '-') return;
        inProcessInput.push(item);
      });
    } else {
      control.value?.split('').forEach(item => {
        if (item === '-') return;
        inProcessInput.push(item);
      });
    }
    transformedInput = inProcessInput.join('');
    const validationRegex = new RegExp(NumberCharPattern.NUMBER);
    if (!validationRegex.test(transformedInput) || !transformedInput && control.value?.length > 0 && transformedInput.length > 0) {
      return {
        alphaNumeric: true
      };
    }
    return null;
  };
};
const MomentValidator = () => {
  return control => {
    if (control.value) {
      const modelValue = control.value + '-1904';
      const date = moment(modelValue.replace('/', '-'), 'MM-DD-YYYY', true);
      return date.isValid() ? null : {
        date: true
      };
    } else {
      return null;
    }
  };
};
const DateRangeValidator = err => {
  const fn = control => {
    const enteredDate = control?.value;
    const parsedDate = DateTime.fromISO(enteredDate);
    const currentDate = DateTime.now();
    const maxPastDate = currentDate.minus({
      years: 100
    });
    if (parsedDate?.isValid) {
      if (parsedDate >= maxPastDate && parsedDate < currentDate.minus({
        days: 1
      })) {
        return null;
      }
      return {
        isValidDate: false
      };
    } else {
      return null;
    }
  };
  return validatorWithMessage(fn, err);
};
const RequiredLabel = (required, label) => {
  return [trimEnd(label, '*')?.trim(), required ? '*' : ''].join(' ');
};
/** One field in the group is required and only one can have a value */
const gdsOnlyOneRequiredValidator = (controlNames, message) => {
  return control => {
    if (!controlNames.length) return null;
    const allControls = controlNames.map(name => control.parent?.get(name)).filter(c => !!c);
    let errors = null;
    const hasRequiredErrors = allControls.every(control => {
      const requiredError = gdsRequiredValidator(message)(control);
      return !!get(requiredError, 'message');
    });
    const moreThanOneHasValue = allControls.map(ctrl => !!ctrl.value?.trim()).filter(c => !!c).length > 1;
    if (moreThanOneHasValue) {
      errors = {
        onlyOneAllowed: true,
        message
      };
    } else if (hasRequiredErrors) {
      errors = gdsRequiredValidator(message)(control);
    }
    const form = control.parent;
    if (!form.__validationInProgress) {
      form.__validationInProgress = true;
      allControls.forEach(ctrl => {
        if (ctrl !== control) {
          const prevErrors = ctrl.errors || {};
          const newErrors = errors ? {
            ...prevErrors,
            ...errors
          } : null;
          if (JSON.stringify(prevErrors) !== JSON.stringify(newErrors)) {
            ctrl.setErrors(newErrors);
            ctrl.updateValueAndValidity({
              onlySelf: true,
              emitEvent: false
            });
          }
        }
      });
      form.__validationInProgress = false;
    }
    return errors;
  };
};

/*
 * Public API Surface of gds-input-helper
 */

/**
 * Generated bundle index. Do not edit.
 */

export { CreditCardPatterns, DateRangeValidator, EmailPasswordPattern, GdsInputHelperDirective, GdsInputHelperModule, MiscPattern, MomentValidator, NamePattern, NumberCharPattern, NumericValidator, PhonePattern, RequiredLabel, ZipCodePattern, formFieldRanges, gdsEmailValidator, gdsMaxLengthValidator, gdsMinLengthValidator, gdsMonthDateValidator, gdsNameValidator, gdsOnlyOneRequiredValidator, gdsPasswordValidator, gdsPatternValidator, gdsPhoneNumericValidator, gdsRequiredValidator, gdsSuffixValidator, gdsVisaCardValidator, validatorWithMessage };
